import React, { useState, useEffect } from 'react';
import { pagePaperSx } from '../../styles/adminPageStyles';
import PageHeader from '../../components/PageHeader';
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Tooltip,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  History as HistoryIcon,
  Refresh as RefreshIcon,
  LockOutlined as LockIcon,
} from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import axios from 'axios';
import { Navigate } from 'react-router-dom';
import { API_BASE_URL } from '../../config/api';

interface Asset {
  asset_id: string;
  asset_name: string;
  number_plate?: string | null;
  mot_status?: string | null;
  mot_due_date?: string | null;
  mot_last_checked_at?: string | null;
  tax_status?: string | null;
  tax_due_date?: string | null;
  tax_last_checked_at?: string | null;
  vehicle_make?: string | null;
  vehicle_model?: string | null;
  vehicle_year?: string | null;
  vehicle_colour?: string | null;
  location_name?: string | null;
}

const getMotStatusCategory = (dueDateStr?: string | null, status?: string | null): 'valid' | 'due_soon' | 'expired' | 'unknown' => {
  if (!status || status === 'No results returned' || status === 'No details held by DVLA') return 'unknown';
  const sl = status.toLowerCase();
  if (sl.includes('not valid') || sl === 'invalid') return 'expired';
  if (!dueDateStr) return 'unknown';
  const due = new Date(dueDateStr);
  const now = new Date();
  const days = Math.floor((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  if (days < 0) return 'expired';
  if (days <= 30) return 'due_soon';
  return 'valid';
};

const getTaxStatusCategory = (dueDateStr?: string | null, status?: string | null): 'valid' | 'due_soon' | 'expired' | 'unknown' => {
  if (!status) return 'unknown';
  const sl = status.toLowerCase();
  if (sl === 'untaxed' || sl === 'sorn') return 'expired';
  if (sl === 'taxed') {
    if (!dueDateStr) return 'valid';
    const due = new Date(dueDateStr);
    const now = new Date();
    const days = Math.floor((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    if (days < 0) return 'expired';
    if (days <= 30) return 'due_soon';
    return 'valid';
  }
  return 'unknown';
};

const MotTax: React.FC = () => {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [allowed, setAllowed] = useState(false);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  /** When feature is off and super admin hid nav, skip lock screen and send user to Assets */
  const [redirectWhenHiddenOff, setRedirectWhenHiddenOff] = useState(false);

  const [motTabSearch, setMotTabSearch] = useState('');
  const [motTabFilter, setMotTabFilter] = useState('all');
  const [motCheckingIds, setMotCheckingIds] = useState<Set<string>>(new Set());
  const [motHistoryModal, setMotHistoryModal] = useState<{
    open: boolean;
    asset: Asset | null;
    history: any[];
    loading: boolean;
    refreshing: boolean;
  }>({ open: false, asset: null, history: [], loading: false, refreshing: false });

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });

  useEffect(() => {
    fetchPermissions();
    fetchAssets();
  }, []);

  const fetchPermissions = async () => {
    try {
      const token = localStorage.getItem('token');
      const businessId = localStorage.getItem('businessId');
      if (!token || !businessId) { setPermissionsLoaded(true); return; }
      const res = await axios.get(`${API_BASE_URL}/businesses/${businessId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const allow = res.data.allow_mot_tax_tab === 1 || res.data.allow_mot_tax_tab === true;
      const hideNav =
        res.data.hide_mot_tax_nav_when_disabled === 1 || res.data.hide_mot_tax_nav_when_disabled === true;
      setAllowed(allow);
      setRedirectWhenHiddenOff(!allow && hideNav);
    } catch {
      // default denied
    } finally {
      setPermissionsLoaded(true);
    }
  };

  const fetchAssets = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE_URL}/assets`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      let list: Asset[] = [];
      if (res.data?.success && Array.isArray(res.data.data)) list = res.data.data;
      else if (Array.isArray(res.data)) list = res.data;
      setAssets(list);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  };

  const openMotHistory = async (asset: Asset) => {
    setMotHistoryModal({ open: true, asset, history: [], loading: true, refreshing: false });
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE_URL}/assets/${asset.asset_id}/mot-history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) {
        setMotHistoryModal(prev => ({
          ...prev,
          asset: { ...asset, ...res.data.data.asset },
          history: res.data.data.history,
          loading: false,
        }));
      } else {
        setMotHistoryModal(prev => ({ ...prev, loading: false }));
      }
    } catch {
      setMotHistoryModal(prev => ({ ...prev, loading: false }));
    }
  };

  const runMotCheckForAsset = async (asset: Asset) => {
    if (motCheckingIds.has(asset.asset_id)) return;
    setMotCheckingIds(prev => new Set(prev).add(asset.asset_id));
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/assets/${asset.asset_id}/refresh-mot`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const res = await axios.get(`${API_BASE_URL}/assets/${asset.asset_id}/mot-history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) {
        const updated = res.data.data.asset;
        setAssets(prev => prev.map(a => a.asset_id === asset.asset_id ? { ...a, ...updated } : a));
        setSnackbar({ open: true, message: `MOT check complete for ${asset.asset_name}`, severity: 'success' });
      }
    } catch {
      setSnackbar({ open: true, message: `Failed to run MOT check for ${asset.asset_name}`, severity: 'error' });
    } finally {
      setMotCheckingIds(prev => { const s = new Set(prev); s.delete(asset.asset_id); return s; });
    }
  };

  const refreshMotCheck = async (assetId: string) => {
    setMotHistoryModal(prev => ({ ...prev, refreshing: true }));
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/assets/${assetId}/refresh-mot`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const res = await axios.get(`${API_BASE_URL}/assets/${assetId}/mot-history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) {
        const updatedAsset = res.data.data.asset;
        setMotHistoryModal(prev => ({
          ...prev,
          asset: { ...(prev.asset as Asset), ...updatedAsset },
          history: res.data.data.history,
          refreshing: false,
        }));
        setAssets(prev => prev.map(a => a.asset_id === assetId ? { ...a, ...updatedAsset } : a));
        setSnackbar({ open: true, message: 'MOT data refreshed', severity: 'success' });
      }
    } catch {
      setMotHistoryModal(prev => ({ ...prev, refreshing: false }));
      setSnackbar({ open: true, message: 'Failed to refresh MOT data', severity: 'error' });
    }
  };

  if (!permissionsLoaded) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!allowed && redirectWhenHiddenOff) {
    return <Navigate to="/admin/assets" replace />;
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!allowed) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 400, gap: 2 }}>
        <LockIcon sx={{ fontSize: 56, color: 'text.disabled' }} />
        <Typography variant="h6" color="text.secondary">MOT / Tax is not enabled for this business</Typography>
        <Typography variant="body2" color="text.disabled">A super admin can enable it under Features &amp; Access in Business Settings.</Typography>
      </Box>
    );
  }

  const platedAssets = assets.filter(a => a.number_plate);
  const motCat = (a: Asset) => getMotStatusCategory(a.mot_due_date, a.mot_status);
  const taxCat = (a: Asset) => getTaxStatusCategory(a.tax_due_date, a.tax_status);

  const filtered = platedAssets.filter(a => {
    const q = motTabSearch.toLowerCase();
    if (q) {
      const hit = (a.asset_name || '').toLowerCase().includes(q) ||
        (a.number_plate || '').toLowerCase().includes(q) ||
        (a.vehicle_make || '').toLowerCase().includes(q) ||
        (a.vehicle_model || '').toLowerCase().includes(q);
      if (!hit) return false;
    }
    if (motTabFilter === 'all') return true;
    const mc = motCat(a);
    const tc = taxCat(a);
    if (motTabFilter === 'attention') return mc === 'expired' || mc === 'due_soon' || tc === 'expired' || tc === 'due_soon';
    if (motTabFilter === 'expired') return mc === 'expired' || tc === 'expired';
    if (motTabFilter === 'due_soon') return (mc === 'due_soon' || tc === 'due_soon') && mc !== 'expired' && tc !== 'expired';
    if (motTabFilter === 'valid') return mc === 'valid' && tc === 'valid';
    if (motTabFilter === 'unchecked') return !a.mot_status && !a.tax_status;
    return true;
  });

  const expiredCount = platedAssets.filter(a => motCat(a) === 'expired' || taxCat(a) === 'expired').length;
  const dueSoonCount = platedAssets.filter(a => (motCat(a) === 'due_soon' || taxCat(a) === 'due_soon') && motCat(a) !== 'expired' && taxCat(a) !== 'expired').length;
  const validCount = platedAssets.filter(a => motCat(a) === 'valid' && taxCat(a) === 'valid').length;
  const uncheckedCount = platedAssets.filter(a => !a.mot_status && !a.tax_status).length;

  const fmtDate = (d?: string | null) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', pt: 0.5, pb: 3 }}>
      <PageHeader title="MOT / Tax" subtitle="Fleet" />
      <Box>
        {/* Summary cards */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          {[
            { label: 'Total Vehicles', count: platedAssets.length, color: 'default', filter: 'all' },
            { label: 'Expired', count: expiredCount, color: 'error', filter: 'expired' },
            { label: 'Due Soon', count: dueSoonCount, color: 'warning', filter: 'due_soon' },
            { label: 'Valid', count: validCount, color: 'success', filter: 'valid' },
            { label: 'Not Checked', count: uncheckedCount, color: 'default', filter: 'unchecked' },
          ].map(({ label, count, color, filter }) => (
            <Paper
              key={filter}
              onClick={() => setMotTabFilter(filter)}
              sx={{
                ...pagePaperSx,
                p: 2, minWidth: 120, flex: '1 1 120px', cursor: 'pointer', textAlign: 'center',
                borderLeft: `3px solid`,
                borderLeftColor: color === 'error' ? 'error.main' : color === 'warning' ? 'warning.main' : color === 'success' ? 'success.main' : 'text.disabled',
                boxShadow: motTabFilter === filter ? 2 : 'none',
                transition: 'all 0.15s',
              }}
            >
              <Typography variant="h4" sx={{
                fontWeight: 800,
                color: color === 'error' ? 'error.main' : color === 'warning' ? 'warning.main' : color === 'success' ? 'success.main' : 'text.primary',
              }}>
                {count}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>{label}</Typography>
            </Paper>
          ))}
        </Box>

        {/* Search + filter row */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search by name, plate, make…"
            value={motTabSearch}
            onChange={e => setMotTabSearch(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />,
              endAdornment: motTabSearch ? (
                <IconButton size="small" onClick={() => setMotTabSearch('')}><ClearIcon fontSize="small" /></IconButton>
              ) : null,
            }}
            sx={{ minWidth: 260 }}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Status Filter</InputLabel>
            <Select value={motTabFilter} label="Status Filter" onChange={e => setMotTabFilter(e.target.value)}>
              <MenuItem value="all">All vehicles</MenuItem>
              <MenuItem value="attention">Needs Attention</MenuItem>
              <MenuItem value="expired">Expired</MenuItem>
              <MenuItem value="due_soon">Due Soon (≤30 days)</MenuItem>
              <MenuItem value="valid">All Valid</MenuItem>
              <MenuItem value="unchecked">Not Yet Checked</MenuItem>
            </Select>
          </FormControl>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
            {filtered.length} vehicle{filtered.length !== 1 ? 's' : ''}
          </Typography>
        </Box>

        {/* Table */}
        <Paper variant="outlined" sx={{ borderRadius: 2 }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Asset</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Plate</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Vehicle</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>MOT Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>MOT Expires</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Tax Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Tax Expires</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Last Checked</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        {platedAssets.length === 0
                          ? 'No assets with a number plate found. Add a plate to an asset to enable MOT/tax checks.'
                          : 'No vehicles match the current filter.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : filtered.map(a => {
                  const mc = motCat(a);
                  const tc = taxCat(a);
                  const motColor = mc === 'expired' ? 'error' : mc === 'due_soon' ? 'warning' : mc === 'valid' ? 'success' : 'default';
                  const taxColor = tc === 'expired' ? 'error' : tc === 'due_soon' ? 'warning' : tc === 'valid' ? 'success' : 'default';
                  const rowBg = (mc === 'expired' || tc === 'expired') ? 'rgba(211,47,47,0.04)'
                    : (mc === 'due_soon' || tc === 'due_soon') ? 'rgba(245,124,0,0.04)'
                    : undefined;
                  return (
                    <TableRow key={a.asset_id} sx={{ bgcolor: rowBg, '&:hover': { bgcolor: 'action.hover' } }}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{a.asset_name}</Typography>
                        <Typography variant="caption" color="text.secondary">{a.location_name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 700, letterSpacing: 1 }}>
                          {(a.number_plate || '').toUpperCase()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {[a.vehicle_make, a.vehicle_model].filter(Boolean).join(' ') || '—'}
                        </Typography>
                        {a.vehicle_year && (
                          <Typography variant="caption" color="text.secondary">{a.vehicle_year}{a.vehicle_colour ? ` · ${a.vehicle_colour}` : ''}</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {a.mot_status ? (
                          <Chip
                            label={mc === 'expired' ? 'Expired' : mc === 'due_soon' ? 'Due Soon' : mc === 'valid' ? 'Valid' : a.mot_status}
                            color={motColor as any}
                            size="small"
                            sx={{ fontWeight: 700, fontSize: '0.7rem' }}
                          />
                        ) : (
                          <Chip label="Not Checked" size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{fmtDate(a.mot_due_date)}</Typography>
                        {mc === 'due_soon' && a.mot_due_date && (
                          <Typography variant="caption" color="warning.dark">
                            {Math.ceil((new Date(a.mot_due_date).getTime() - Date.now()) / 86400000)} days
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {a.tax_status ? (
                          <Chip
                            label={tc === 'expired' ? 'Untaxed' : tc === 'due_soon' ? 'Due Soon' : tc === 'valid' ? 'Taxed' : a.tax_status}
                            color={taxColor as any}
                            size="small"
                            sx={{ fontWeight: 700, fontSize: '0.7rem' }}
                          />
                        ) : (
                          <Chip label="Not Checked" size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{fmtDate(a.tax_due_date)}</Typography>
                        {tc === 'due_soon' && a.tax_due_date && (
                          <Typography variant="caption" color="warning.dark">
                            {Math.ceil((new Date(a.tax_due_date).getTime() - Date.now()) / 86400000)} days
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {a.mot_last_checked_at ? new Date(a.mot_last_checked_at).toLocaleDateString('en-GB') : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                          <Tooltip title="View MOT History">
                            <IconButton size="small" onClick={() => openMotHistory(a)}>
                              <HistoryIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={motCheckingIds.has(a.asset_id) ? 'Checking…' : 'Refresh MOT / Tax'}>
                            <span>
                              <IconButton
                                size="small"
                                disabled={motCheckingIds.has(a.asset_id)}
                                onClick={() => runMotCheckForAsset(a)}
                              >
                                {motCheckingIds.has(a.asset_id)
                                  ? <CircularProgress size={16} />
                                  : <RefreshIcon fontSize="small" />}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      {/* MOT History Modal */}
      <Dialog
        open={motHistoryModal.open}
        onClose={() => setMotHistoryModal(prev => ({ ...prev, open: false }))}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryIcon />
            <Box>
              <Typography variant="h6" component="span">MOT &amp; Tax History</Typography>
              {motHistoryModal.asset?.asset_name && (
                <Typography variant="body2" color="text.secondary">
                  {motHistoryModal.asset.asset_name}
                  {motHistoryModal.asset.number_plate && ` · ${motHistoryModal.asset.number_plate}`}
                  {motHistoryModal.asset.vehicle_make && ` · ${motHistoryModal.asset.vehicle_make} ${motHistoryModal.asset.vehicle_model || ''}`}
                  {motHistoryModal.asset.vehicle_year && ` (${motHistoryModal.asset.vehicle_year})`}
                  {motHistoryModal.asset.vehicle_colour && ` · ${motHistoryModal.asset.vehicle_colour}`}
                </Typography>
              )}
            </Box>
          </Box>
          {motHistoryModal.asset?.number_plate && (
            <Tooltip title="Refresh MOT data from DVSA">
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={motHistoryModal.refreshing ? <CircularProgress size={14} /> : <RefreshIcon />}
                  disabled={motHistoryModal.refreshing || motHistoryModal.loading}
                  onClick={() => motHistoryModal.asset && refreshMotCheck(motHistoryModal.asset.asset_id)}
                >
                  Refresh
                </Button>
              </span>
            </Tooltip>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {motHistoryModal.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {motHistoryModal.asset && (motHistoryModal.asset.mot_status || motHistoryModal.asset.tax_status) && (
                <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                  {motHistoryModal.asset.mot_status && (() => {
                    const cat = getMotStatusCategory(motHistoryModal.asset!.mot_due_date, motHistoryModal.asset!.mot_status);
                    const color = cat === 'expired' ? 'error' : cat === 'due_soon' ? 'warning' : 'success';
                    return (
                      <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 180, borderColor: `${color}.main`, borderWidth: 2 }}>
                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>MOT Status</Typography>
                        <Chip label={cat === 'expired' ? 'Expired' : cat === 'due_soon' ? 'Due Soon' : 'Valid'} color={color as any} size="small" sx={{ fontWeight: 700, mb: 0.5 }} />
                        <Typography variant="body2" sx={{ mt: 0.5 }}>{motHistoryModal.asset.mot_status}</Typography>
                        {motHistoryModal.asset.mot_due_date && (
                          <Typography variant="body2" color="text.secondary">
                            Due: {new Date(motHistoryModal.asset.mot_due_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                          </Typography>
                        )}
                        {motHistoryModal.asset.mot_last_checked_at && (
                          <Typography variant="caption" color="text.secondary">
                            Checked: {new Date(motHistoryModal.asset.mot_last_checked_at).toLocaleString()}
                          </Typography>
                        )}
                      </Paper>
                    );
                  })()}
                  {motHistoryModal.asset.tax_status && (() => {
                    const cat = getTaxStatusCategory(motHistoryModal.asset!.tax_due_date, motHistoryModal.asset!.tax_status);
                    const color = cat === 'expired' ? 'error' : cat === 'due_soon' ? 'warning' : 'success';
                    return (
                      <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 180, borderColor: `${color}.main`, borderWidth: 2 }}>
                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>Road Tax</Typography>
                        <Chip label={cat === 'expired' ? 'Untaxed' : cat === 'due_soon' ? 'Due Soon' : 'Taxed'} color={color as any} size="small" sx={{ fontWeight: 700, mb: 0.5 }} />
                        <Typography variant="body2" sx={{ mt: 0.5 }}>{motHistoryModal.asset.tax_status}</Typography>
                        {motHistoryModal.asset.tax_due_date && (
                          <Typography variant="body2" color="text.secondary">
                            Due: {new Date(motHistoryModal.asset.tax_due_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                          </Typography>
                        )}
                        {motHistoryModal.asset.tax_last_checked_at && (
                          <Typography variant="caption" color="text.secondary">
                            Checked: {new Date(motHistoryModal.asset.tax_last_checked_at).toLocaleString()}
                          </Typography>
                        )}
                      </Paper>
                    );
                  })()}
                </Box>
              )}

              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>MOT Test History</Typography>
              {motHistoryModal.history.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No MOT history found.
                  {motHistoryModal.asset?.number_plate && ' Click Refresh to fetch from DVSA.'}
                </Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ background: 'action.hover' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Result</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Expiry</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Mileage</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Defects</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {motHistoryModal.history.map((test: any, idx: number) => {
                        const defects = Array.isArray(test.defects) ? test.defects : [];
                        const majors = defects.filter((d: any) => d.type === 'MAJOR' || d.dangerous);
                        const advisories = defects.filter((d: any) => d.type === 'ADVISORY' && !d.dangerous);
                        return (
                          <TableRow key={test.id || idx} sx={{ verticalAlign: 'top' }}>
                            <TableCell>
                              {test.completed_date
                                ? new Date(test.completed_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                                : '—'}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={test.test_result === 'PASSED' ? 'Pass' : test.test_result === 'FAILED' ? 'Fail' : test.test_result || '—'}
                                color={test.test_result === 'PASSED' ? 'success' : test.test_result === 'FAILED' ? 'error' : 'default'}
                                size="small"
                                sx={{ fontWeight: 700, fontSize: '0.7rem' }}
                              />
                            </TableCell>
                            <TableCell>
                              {test.expiry_date
                                ? new Date(test.expiry_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                                : '—'}
                            </TableCell>
                            <TableCell>
                              {test.odometer_value
                                ? `${Number(test.odometer_value).toLocaleString()} ${test.odometer_unit || 'mi'}`
                                : '—'}
                            </TableCell>
                            <TableCell>
                              {defects.length === 0 ? (
                                <Typography variant="caption" color="text.secondary">None</Typography>
                              ) : (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                  {majors.length > 0 && (
                                    <Box>
                                      <Typography variant="caption" color="error.dark" sx={{ fontWeight: 700, display: 'block' }}>
                                        Major / Dangerous ({majors.length})
                                      </Typography>
                                      {majors.map((d: any, i: number) => (
                                        <Typography key={i} variant="caption" display="block" color="text.secondary" sx={{ pl: 1 }}>
                                          • {d.text}
                                        </Typography>
                                      ))}
                                    </Box>
                                  )}
                                  {advisories.length > 0 && (
                                    <Box>
                                      <Typography variant="caption" color="warning.dark" sx={{ fontWeight: 700, display: 'block' }}>
                                        Advisory ({advisories.length})
                                      </Typography>
                                      {advisories.map((d: any, i: number) => (
                                        <Typography key={i} variant="caption" display="block" color="text.secondary" sx={{ pl: 1 }}>
                                          • {d.text}
                                        </Typography>
                                      ))}
                                    </Box>
                                  )}
                                </Box>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMotHistoryModal(prev => ({ ...prev, open: false }))}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MotTax;
